import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Heart, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import DonateDialog from "@/components/DonateDialog";
import { getTestimonials } from "@/lib/api";
import { IMG, MINISTRY } from "@/lib/content";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => { getTestimonials().then(setItems).catch(() => {}); }, []);

  return (
    <>
      <PageHero testid="testimonials-hero" kicker="Testimonials" title="Lives changed, in their own words" subtitle="Behind every program is a person whose life was touched by generosity and grace." image={IMG.mentorWomen2} />

      {/* FEATURED VIDEO TESTIMONY */}
      <section className="py-16 sm:py-20 relative z-10" data-testid="video-testimony">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <span className="text-gold-dark text-sm tracking-[0.22em] uppercase font-semibold">Watch &amp; Be Encouraged</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-plum font-semibold mt-3">A testimony of God's faithfulness</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-plum/10 bg-plum-dark">
              <video
                data-testid="testimony-video"
                controls
                playsInline
                preload="metadata"
                poster={IMG.hero}
                className="w-full h-auto max-h-[70vh] bg-plum-dark"
              >
                <source src={MINISTRY.videoTestimonial} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {items.map((t, i) => (
              <div key={t.id || i}>
                <div className="h-full bg-white rounded-2xl border border-plum/10 p-8 lift flex flex-col" data-testid={`testimonial-${i}`}>
                  <Quote className="text-gold/40" size={40} />
                  <p className="font-heading text-2xl text-plum leading-snug italic mt-4 flex-1">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-4 mt-7 pt-6 border-t border-plum/10">
                    {t.image
                      ? <img src={t.image} alt={t.program} className="w-14 h-14 rounded-full object-cover" />
                      : <span className="grid place-items-center w-12 h-12 rounded-full bg-plum/5 text-gold-dark shrink-0"><Quote size={20} /></span>}
                    <div>
                      {t.name && <p className="font-semibold text-plum">{t.name}</p>}
                      <p className="text-stone text-sm">{t.role} &bull; {t.program}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Reveal className="mt-20 bg-plum-dark rounded-3xl p-10 sm:p-16 text-center">
            <Heart className="text-gold mx-auto mb-5" size={40} />
            <h2 className="font-heading text-3xl sm:text-4xl text-cream font-semibold">Write the next testimony</h2>
            <p className="text-cream/80 mt-4 text-lg max-w-xl mx-auto">Your partnership creates the stories we'll celebrate tomorrow.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <DonateDialog trigger={<button className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="testimonials-donate-btn"><Heart size={18} /> Donate Now</button>} />
              <Link to="/get-involved" className="border border-cream/40 text-cream rounded-full px-8 py-4 hover:bg-cream/10 transition-colors flex items-center gap-2" data-testid="testimonials-involved-btn">Get Involved <ArrowRight size={17} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
